import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import recommandedIcon from "../assets/icons/reccomnadedIocn.png";
import popularIcon from "../assets/icons/popularIocn.png";
import addIcon from "../assets/icons/addIcon1.png";
import "../css/EnterpriseDetails.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import editIcon from "../assets/Images/Edit.png";
import cpyIcon from "../assets/icons/Copy.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { toast } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import Cropper from "react-easy-crop";
import getCroppedImg from "../utils/cropImage";
import EnterpriseTenantKeySection from "../components/EnterpriseTenantKeySection";
import EnterpriseProfileSection from "../components/enterprise-profile/EnterpriseProfileSection";
import TemplateSettingsPanel from "../components/template-settings/TemplateSettingsPanel";
import AllowedExtensionsEditor from "../components/AllowedExtensionsEditor";
import {
  colorTemplates,
  defaultThemeTemplate,
  defaultThemeSettings,
  mapTemplateColorsToThemeSettings,
  mapThemeSettingsToTemplateColors,
  templateColorsMatch,
} from "../components/template-settings/templateSettingsConfig";

const API_BASE = process.env.REACT_APP_API_URL;

const EnterpriseSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const enterprise = location.state?.enterprise;
  const enterpriseId =
    enterprise?.enterprise_id ||
    location.state?.enterpriseId ||
    sessionStorage.getItem("enterprise_id") ||
    "";
  const enterpriseName =
    enterprise?.enterprise_name ||
    sessionStorage.getItem("enterprise_name") ||
    "";

  const [showSubscription, setShowSubscription] = useState(true);
  const [showOrganization, setShowOrganization] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showModelSettings, setShowModelSettings] = useState(true);
  const [existingModelSettings, setExistingModelSettings] = useState([]);
  const [isModelSettingsFetched, setIsModelSettingsFetched] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(true);
  const [documentation, setDocumentation] = useState({
    auto_approve: false,
    max_upload_file_count: 20,
    max_upload_zip_size_mb: 400,
    max_upload_document_size_mb: 100,
    max_upload_media_size_mb: 50,
    max_upload_other_size_mb: 50,
    project_capacity_limit_value: 75,
    project_capacity_limit_unit: "MB",
    project_max_files_per_project: 20,
    allowed_upload_extensions: null,
  });
  const [hasDocId, setHasDocId] = useState(false); // For backend check

  // === Chat Security Settings (messages only) ===
  const [showChatSecurity, setShowChatSecurity] = useState(true);
  const [chatSecurity, setChatSecurity] = useState({
    chat_encryption_mode: "none", // none | standard | end_to_end
    chat_key_management_mode: "platform_managed", // platform_managed | customer_managed_byok
    byok_key_base64: "",
  });
  const [isChatSecurityLoading, setIsChatSecurityLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [modelSettings, setModelSettings] = useState({
    recommended_models: [],
    popular_models: [],
  });

  const [showTemplateSettings, setShowTemplateSettings] = useState(true);
  const [themeSettings, setThemeSettings] = useState(defaultThemeSettings);
  const [templateColors, setTemplateColors] = useState(
    mapThemeSettingsToTemplateColors(defaultThemeSettings),
  );

  // === Glossary Settings ===
  const [showGlossary, setShowGlossary] = useState(true);
  const [glossary, setGlossary] = useState([]);
  const [isGlossaryFetched, setIsGlossaryFetched] = useState(false);
  const [isGlossaryLoading, setIsGlossaryLoading] = useState(false);
  const [isGlossarySaving, setIsGlossarySaving] = useState(false);
  const [isGlossaryDeleting, setIsGlossaryDeleting] = useState(false);

  const [glossarySearchTerm, setGlossarySearchTerm] = useState("");
  const [glossaryCurrentPage, setGlossaryCurrentPage] = useState(1);
  const [glossaryEntriesPerPage, setGlossaryEntriesPerPage] = useState(10);
  // const [newGlossaryTerm, setNewGlossaryTerm] = useState({ term: "", full_form: "", description: "" });

  const filteredGlossary = useMemo(() => {
    if (!glossarySearchTerm) return glossary;
    const lowerSearch = glossarySearchTerm.toLowerCase();
    return glossary.filter(
      (item) =>
        item.term?.toLowerCase().includes(lowerSearch) ||
        item.full_form?.toLowerCase().includes(lowerSearch) ||
        item.description?.toLowerCase().includes(lowerSearch),
    );
  }, [glossary, glossarySearchTerm]);

  const totalGlossaryPages =
    Math.ceil(filteredGlossary.length / glossaryEntriesPerPage) || 1;
  const paginatedGlossary = useMemo(() => {
    const startIndex = (glossaryCurrentPage - 1) * glossaryEntriesPerPage;
    return filteredGlossary
      .slice(startIndex, startIndex + glossaryEntriesPerPage)
      .map((item) => {
        // Map to the original index in the main array for edits/deletes
        return { ...item, originalIndex: glossary.indexOf(item) };
      });
  }, [filteredGlossary, glossaryCurrentPage, glossaryEntriesPerPage, glossary]);

  const [availableModels, setAvailableModels] = useState([]);

  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("OpenAI");
  const [apiKey, setApiKey] = useState("");
  const [apiNotes, setApiNotes] = useState("");
  const [apiKeys, setApiKeys] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingThemeOnly, setIsSavingThemeOnly] = useState(false);
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [isProcessingLogo, setIsProcessingLogo] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");
  const presetTemplates = [defaultThemeTemplate, ...colorTemplates];

  const fetchModels = async () => {
    try {
      // console.log("🔹 Fetching available models...");
      const res = await api.get(
        `${API_BASE}/enterprise-model-settings/enterprise-active-model-settings`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // console.log("📌 Available models API response:", res.data);

      const available = res.data.data.filter(
        (item) =>
          item.model_details && // ✅ prevent null
          item.model_details.server_status === true && // ✅ prevent inactive server
          item.model_details.model_download_status === "1" &&
          item.model_details.model_status === "Approved",
      );

      // console.log("🎯 Filtered available models:", available);

      setAvailableModels(available);
    } catch (err) {
      console.error("❌ Error fetching available models:", err);
      showErrorToast(TOAST_MESSAGES.MODEL.FETCH_ERROR);
    }
  };

  const fetchApiKeys = async () => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const enterpriseFid = enterpriseId;

    // ✅ Ensure token and enterprise ID exist
    if (!token || !enterpriseFid) {
      console.warn("Token or enterprise ID missing, cannot fetch API keys");
      return;
    }

    try {
      const res = await api.get(`${API_BASE}/enterprise-api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { enterprise_fid: enterpriseFid }, // pass enterprise ID
      });

      // ✅ Log cleanly for debugging
      // console.log("Fetched API keys:", res.data);

      // ✅ Update state safely
      setApiKeys(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch API keys:", err);
      showErrorToast("Failed to fetch API keys");
    }
  };

  // 1️⃣ Fetch available models
  useEffect(() => {
    fetchModels();
    fetchApiKeys();
  }, []);

  // === Glossary API helpers ===
  const fetchGlossary = async () => {
    const id = enterpriseId;
    if (!id) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    if (!token) return;

    setIsGlossaryLoading(true);
    try {
      const res = await api.get(`${API_BASE}/enterprise/glossary/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setGlossary(res.data?.glossary || []);
      setIsGlossaryFetched(true);
    } catch (err) {
      console.error("Failed to fetch glossary:", err);
    } finally {
      setIsGlossaryLoading(false);
    }
  };

  // const handleAddTermInline = (e) => {
  //   e.preventDefault();
  //   if (!newGlossaryTerm.term?.trim() || !newGlossaryTerm.full_form?.trim()) {
  //     showErrorToast("Term and Full Form are required");
  //     return;
  //   }
  //   // Check for duplicates
  //   const isDuplicate = glossary.some((item) => item.term?.toLowerCase() === newGlossaryTerm.term.trim().toLowerCase());
  //   if (isDuplicate) {
  //     showErrorToast("This term already exists in the glossary");
  //     return;
  //   }

  //   // Add to the top of the glossary array
  //   setGlossary((prev) => [{ ...newGlossaryTerm }, ...prev]);
  //   setNewGlossaryTerm({ term: "", full_form: "", description: "" });
  // };

  const handleAddGlossaryRow = () => {
    setGlossary((prev) => [
      ...prev,
      {
        term: "",
        full_form: "",
        description: "",
      },
    ]);
  };

  const handleRemoveGlossaryRow = (originalIndex) => {
    setGlossary((prev) => prev.filter((_, i) => i !== originalIndex));
  };

  const handleGlossaryChange = (originalIndex, key, value) => {
    setGlossary((prev) =>
      prev.map((item, i) =>
        i === originalIndex ? { ...item, [key]: value } : item,
      ),
    );
  };

  const handleSaveGlossary = async () => {
    if (isGlossarySaving) return;
    const glossaryToSave = glossary.filter(
      (item) =>
        item.term.trim() || item.full_form.trim() || item.description.trim(),
    );
    const id = enterpriseId;
    if (!id) return showErrorToast("Enterprise ID missing");

    // Validation: no empty term/full_form, no duplicate terms
    const seen = new Set();
    for (let i = 0; i < glossaryToSave.length; i++) {
      const item = glossaryToSave[i] || {};
      const term = (item.term || "").trim();
      const full = (item.full_form || "").trim();
      if (!term) return showErrorToast(`Term is required for row ${i + 1}`);
      if (!full)
        return showErrorToast(`Full Form is required for row ${i + 1}`);
      const low = term.toLowerCase();
      if (seen.has(low)) return showErrorToast(`Duplicate term: ${term}`);
      seen.add(low);
    }

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    if (!token) return showErrorToast("Authentication required");

    setIsGlossarySaving(true);
    try {
      let glossary = glossaryToSave;
      await api.post(
        `${API_BASE}/enterprise/glossary/save`,
        { enterprise_id: id, glossary },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showSuccessToast("Glossary saved successfully");
      await fetchGlossary();
    } catch (err) {
      console.error("Failed to save glossary:", err);
      showErrorToast(err.response?.data?.message || "Failed to save glossary");
    } finally {
      setIsGlossarySaving(false);
    }
  };

  const handleDeleteGlossary = async () => {
    const id = enterpriseId;
    if (!id) return showErrorToast("Enterprise ID missing");

    const confirmDel = window.confirm(
      "Are you sure you want to delete all glossary entries for this enterprise?",
    );
    if (!confirmDel) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    if (!token) return showErrorToast("Authentication required");

    setIsGlossaryDeleting(true);
    try {
      await api.delete(`${API_BASE}/enterprise/glossary/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGlossary([]);
      showSuccessToast("Glossary deleted successfully");
      setIsGlossaryFetched(false);
    } catch (err) {
      console.error("Failed to delete glossary:", err);
      showErrorToast(
        err.response?.data?.message || "Failed to delete glossary",
      );
    } finally {
      setIsGlossaryDeleting(false);
    }
  };

  // 2️⃣ Fetch existing enterprise model settings
  useEffect(() => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    if (!token || !enterpriseId) return;

    const fetchSettings = async () => {
      try {
        // console.log("🔹 Fetching existing enterprise model settings...");
        const res = await api.get(
          `${API_BASE}/enterprise-admin-setting/byEnterprise/${enterpriseId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // console.log("📌 Existing model settings API response:", res.data);

        setExistingModelSettings(res.data || []);
        setIsModelSettingsFetched(true);
      } catch (err) {
        console.error("❌ Error fetching existing settings:", err);
      }
    };

    fetchSettings();
  }, [enterpriseId]);

  // 3️⃣ Combine available models and existing settings
  useEffect(() => {
  //   console.log("🔄 Combining models after both APIs fetched...");
  //   console.log("Available models:", availableModels);
  //   console.log("Existing model settings:", existingModelSettings);
  //   console.log("Is settings fetched?", isModelSettingsFetched);

    if (!availableModels.length || !isModelSettingsFetched) return;

    const recommendedModelsFromDB = existingModelSettings
      .filter((item) => item.enterprise_user_model_setting_status == "1")
      .map((item) => item.model_fid?.trim()) // use model_fid
      .filter(Boolean);

    // console.log("recommanded model from db ", recommendedModelsFromDB);

    setModelSettings((prev) => ({
      ...prev,
      recommended_models: recommendedModelsFromDB,
    }));
  }, [availableModels, existingModelSettings, isModelSettingsFetched]);

  const [subscription, setSubscription] = useState({
    enterprise_subscription_name: "",
    enterprise_subscription_duration: "",
    enterprise_subscription_prize: "",
    enterprise_subscription_start_date: "",
    enterprise_subscription_end_date: "",
  });

  const [organization, setOrganization] = useState({
    enterprise_organisation_settings_theme: "",
    can_admin_view_chat: "",
    can_power_user_view_chat: "",
    data_retention_period: "",
    allowed_doc_size_mb: "",
    training_frequency: "",
    enterprise_organisation_settings_selected_model: "",
  });

  const [disclaimer, setDisclaimer] = useState({
    enterprise_disclaimer_title: "",
    enterprise_disclaimer_display_location: "",
    enterprise_disclaimer_description: "",
  });

  const [hasSubId, setHasSubId] = useState(false);
  const [hasOrgId, setHasOrgId] = useState(false);
  const [hasDisId, setHasDisId] = useState(false);

  const handleInput = (setter, key) => (e) =>
    setter((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    const id = enterpriseId;
    if (!id) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    api
      .get(`${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasSubId(true);
          setSubscription(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch subscription failed", err);
      });

    api
      .get(`${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasOrgId(true);
          setOrganization(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch organization settings failed", err);
      });

    // api
    //   .get(`${API_BASE}/enterprise-disclaimer/byEnterprise/${id}`, {
    //     headers,
    //   })
    //   .then((res) => {
    //     if (res.data) {
    //       setHasDisId(true);
    //       setDisclaimer(res.data);
    //     }
    //   })
    //   .catch((err) => {
    //     console.error("Fetch disclaimer failed", err);
    //   });

    api
      .get(`${API_BASE}/enterprise-document-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasDocId(true);
          setDocumentation(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch documentation settings failed", err);
      });

    // === Chat Security Settings (messages only) ===
    setIsChatSecurityLoading(true);
    api
      .get(`${API_BASE}/enterprise-chat-security-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          const normalizedMode = String(
            res.data.chat_encryption_mode || "none",
          ).toLowerCase();
          const normalizedKeyMgmt = String(
            res.data.chat_key_management_mode || "platform_managed",
          ).toLowerCase();

          setChatSecurity((prev) => ({
            ...prev,
            chat_encryption_mode: normalizedMode,
            chat_key_management_mode:
              normalizedMode === "none"
                ? "platform_managed"
                : normalizedKeyMgmt,
            byok_key_base64: "",
          }));
        }
      })
      .catch((err) => {
        console.error("Fetch chat security settings failed", err);
      })
      .finally(() => setIsChatSecurityLoading(false));

    setIsLoadingTheme(true);
    api
      .get(`${API_BASE}/theme/${id}`, {
        headers,
      })
      .then((res) => {
        setIsLoadingTheme(false);
        // console.log("Fetch theme response for enterprise", id, res && res.data);
        if (res.data) {
          // console.log("theme.logo_path:", res.data.logo_path, "theme.logo_key:", res.data.logo_key);
          // console.log("REACT_APP_SERVER_URL:", process.env.REACT_APP_SERVER_URL);
          setThemeSettings(res.data);
          const savedTemplateColors = mapThemeSettingsToTemplateColors(
            res.data,
          );
          setTemplateColors(savedTemplateColors);

          // Use the saved template_name when available.
          const savedTemplateName = res.data.template_name || "";
          if (savedTemplateName === "") {
            setSelectedTemplate("default");
          } else if (savedTemplateName === "custom") {
            setSelectedTemplate("custom");
          } else if (
            presetTemplates.some(
              (template) => template.id === savedTemplateName,
            )
          ) {
            setSelectedTemplate(savedTemplateName);
          } else {
            const matchedTemplate = presetTemplates.find((template) =>
              templateColorsMatch(template.colors, savedTemplateColors),
            );
            setSelectedTemplate(
              matchedTemplate ? matchedTemplate.id : "custom",
            );
          }

          if (res.data.logo_path) {
            const path = res.data.logo_path;
            const constructed = path.startsWith("http")
              ? path
              : `${process.env.REACT_APP_SERVER_URL}/${path.replace(/\\/g, "/")}`;
            // console.log("Using logo_path -> constructed preview URL:", constructed);
            if (path.startsWith("http")) {
              setLogoPreviewUrl(path);
            } else {
              setLogoPreviewUrl(constructed);
            }
            // console.log("Set logoPreviewUrl (from logo_path):", constructed);
          } else if (res.data.logo_key) {
            const keyPath = res.data.logo_key;
            const constructedKey = keyPath.startsWith("http")
              ? keyPath
              : `${process.env.REACT_APP_SERVER_URL}/${String(keyPath).replace(/\\/g, "/")}`;
            // console.log("Using logo_key -> constructed preview URL:", constructedKey);
            setLogoPreviewUrl(constructedKey);
            // console.log("Set logoPreviewUrl (from logo_key):", constructedKey);
          } else {
            // console.log("No logo_path or logo_key returned for enterprise", id);
          }
        } else {
          setThemeSettings(defaultThemeSettings);
          setTemplateColors(
            mapThemeSettingsToTemplateColors(defaultThemeSettings),
          );
          setSelectedTemplate("default");
          setLogoPreviewUrl(null);
        }
      })
      .catch((err) => {
        setIsLoadingTheme(false);
        console.error("❌ Fetch theme settings failed:", err);
      });

    // Fetch glossary for this enterprise
    fetchGlossary();
  }, [enterpriseId]);

  const saveThemeSettings = async () => {
    const id = enterpriseId;
    if (!id) {
      showErrorToast(
        "Enterprise ID is missing. Please reopen this page from an enterprise record.",
      );
      return;
    }

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const updatedThemeSettings = {
      ...themeSettings,
      ...mapTemplateColorsToThemeSettings(templateColors),
      enterprise_name: enterpriseName || themeSettings.enterprise_name || "",
      template_name: selectedTemplate === "default" ? "" : selectedTemplate,
    };

    if (logoFile) {
      const formData = new FormData();
      formData.append("logo", logoFile);
      formData.append("enterprise_id", id);

      const uploadRes = await api.post(
        `${API_BASE}/theme/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // console.log("upload logo response:", uploadRes && uploadRes.data);

      updatedThemeSettings.logo_path = uploadRes.data.logo_path;
      updatedThemeSettings.logo_key = uploadRes.data.logo_key;
    }

    const response = await api.put(
      `${API_BASE}/theme/${id}`,
      { ...updatedThemeSettings, enterprise_id: id },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    setThemeSettings(updatedThemeSettings);

    return response;
  };

  const handleTemplateSave = async () => {
    if (isSavingThemeOnly) return;
    setIsSavingThemeOnly(true);

    try {
      await saveThemeSettings();
      showSuccessToast("Template settings saved successfully");
    } catch (err) {
      console.error("Theme save failed:", err);
      showErrorToast("Failed to save template settings");
    } finally {
      setIsSavingThemeOnly(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    const id = enterpriseId;
    if (!id) {
      showErrorToast(
        "Enterprise ID is missing. Please reopen this page from an enterprise record.",
      );
      setIsSaving(false);
      return;
    }

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const headers = { Authorization: `Bearer ${token}` };

    try {
      // === Subscription ===
      if (hasSubId) {
        await api.put(
          `${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-subscriptions`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      }

      // === Organization ===
      if (hasOrgId) {
        await api.put(
          `${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-organisation-settings`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      }

      // === Documentation Settings ===
      if (hasDocId) {
        await api.put(
          `${API_BASE}/enterprise-document-settings/byEnterprise/${id}`,
          { ...documentation, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-document-settings`,
          { ...documentation, enterprise_fid: id },
          { headers },
        );
      }

      // === Theme Settings ===
      await saveThemeSettings();

      // === Chat Security Settings (messages only) ===
      const chatEncryptionMode = String(
        chatSecurity.chat_encryption_mode || "none",
      ).toLowerCase();
      const chatKeyManagementModeRaw = String(
        chatSecurity.chat_key_management_mode || "platform_managed",
      ).toLowerCase();
      const chatKeyManagementMode =
        chatEncryptionMode === "none"
          ? "platform_managed"
          : chatKeyManagementModeRaw;

      await api.put(
        `${API_BASE}/enterprise-chat-security-settings/byEnterprise/${id}`,
        {
          chat_encryption_mode: chatEncryptionMode,
          chat_key_management_mode: chatKeyManagementMode,
          ...(chatKeyManagementMode === "customer_managed_byok" &&
          chatSecurity.byok_key_base64
            ? { byok_key_base64: chatSecurity.byok_key_base64 }
            : {}),
        },
        { headers },
      );

      // === Model Settings ===
      const modelSettingPayloads = [];
      const processedModelIds = new Set();

      modelSettings.recommended_models.forEach((modelName) => {
        const foundModel = availableModels.find(
          (m) => m.model_details?.model_name === modelName,
        );
        if (!foundModel) return;

        const modelId = foundModel.model_details.model_id;
        processedModelIds.add(modelId);

        const existing = existingModelSettings.find(
          (ems) => ems.model_fid === modelId,
        );

        if (existing) {
          modelSettingPayloads.push(
            api.put(
              `${API_BASE}/enterprise-admin-setting/byEnterprise/${id}`,
              { model_fid: modelId, enterprise_model_setting_status: "1" },
              { headers },
            ),
          );
        } else {
          modelSettingPayloads.push(
            api.post(
              `${API_BASE}/enterprise-admin-setting`,
              {
                enterprise_fid: id,
                model_fid: modelId,
                enterprise_model_setting_status: "1",
              },
              { headers },
            ),
          );
        }
      });

      existingModelSettings.forEach((existing) => {
        if (!processedModelIds.has(existing.model_fid)) {
          modelSettingPayloads.push(
            api.put(
              `${API_BASE}/enterprise-admin-setting/byEnterprise/${id}`,
              {
                model_fid: existing.model_fid,
                enterprise_model_setting_status: "0",
              },
              { headers },
            ),
          );
        }
      });

      if (modelSettingPayloads.length > 0) {
        await Promise.all(modelSettingPayloads);
      }

      showSuccessToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_SUCCESS);
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      console.error("Save failed:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setThemeSettings(defaultThemeSettings);
    setTemplateColors(mapThemeSettingsToTemplateColors(defaultThemeSettings));
    setSelectedTemplate("default");
    setLogoFile(null); // Also reset the logo file
    setLogoPreviewUrl(null);
  };

  const applyTemplateColors = (colors) => {
    setTemplateColors(colors);
    setThemeSettings((prev) => ({
      ...prev,
      ...mapTemplateColorsToThemeSettings(colors),
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId === "custom") return;

    const template = presetTemplates.find((item) => item.id === templateId);
    if (!template) return;

    applyTemplateColors(template.colors);
  };

  const updateTemplateColorSetting = (key, value) => {
    setSelectedTemplate("custom");
    setTemplateColors((prev) => {
      const nextColors = {
        ...prev,
        [key]: value,
      };

      setThemeSettings((currentTheme) => ({
        ...currentTheme,
        ...mapTemplateColorsToThemeSettings(nextColors),
      }));

      return nextColors;
    });
  };

  const updateThemeSetting = (key, value) => {
    setThemeSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleValidateApi = async () => {
    const enterprise_fid = enterpriseId;
    if (!apiKey.trim()) return showErrorToast("API Key is required!");
    if (!selectedProvider) return showErrorToast("Please select a provider!");

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    try {
      setIsValidating(true);

      /* ===============================
       1️⃣ STEP 1: VALIDATE API KEY
    =============================== */
      const validateRes = await api.post(
        `${process.env.REACT_APP_API_URL}/enterprise-model-settings/validate-and-assign`,
        {
          provider: selectedProvider.toLowerCase(),
          api_key: apiKey,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // console.log("✅ API Validation Success:", validateRes.data);

      /* ===============================
       2️⃣ STEP 2: SAVE OR UPDATE
    =============================== */
      const payload = {
        provider_name: selectedProvider,
        api_key: apiKey,
        enterprise_fid,
      };

      if (editingRecord) {
        // 🔁 UPDATE (only after validation success)
        await api.put(
          `${API_BASE}/enterprise-api-keys/${editingRecord.enterprise_api_key_setting_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        showSuccessToast(`${selectedProvider} API Key Updated Successfully ✔`);
      } else {
        // ➕ CREATE
        await api.post(`${API_BASE}/enterprise-api-keys`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        showSuccessToast(`${selectedProvider} API Key Added Successfully ✔`);
      }

      /* ===============================
       3️⃣ FINAL SUCCESS CLEANUP
    =============================== */
      fetchApiKeys(); // refresh table
      setShowApiModal(false); // close modal
      setApiKey(""); // clear input
      setEditingRecord(null); // reset edit state
    } catch (error) {
      console.error("❌ API validation or save failed:", error);

      const msg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "API validation or save failed!";

      // 🔒 IMPORTANT:
      // We DO NOT clear apiKey
      // We DO NOT close modal
      // Old key remains safe in DB
      showErrorToast(msg);
    } finally {
      setIsValidating(false);
    }
  };

  const maskApiKey = (key) => {
    if (!key) return "—";

    const visibleStart = 4; // show first 4 chars
    const visibleEnd = 4; // show last 4 chars

    if (key.length <= visibleStart + visibleEnd) return key; // short keys, show fully

    const maskedMiddle = "*".repeat(key.length - visibleStart - visibleEnd);
    return key.slice(0, visibleStart) + maskedMiddle + key.slice(-visibleEnd);
  };

  const handleEdit = (row) => {
    // Store full record for update flow
    setEditingRecord(row);

    // Provider is fixed during update (disabled in modal)
    setSelectedProvider(row.provider_name);

    // 🔐 Never prefill API key for security
    setApiKey("");

    // Open modal
    setShowApiModal(true);
  };

  const handleChangeStatus = async (row) => {
    try {
      const newStatus = row.enterprise_api_key_status === "1" ? "0" : "1";

      await api.patch(
        `${API_BASE}/enterprise-api-keys/${row.enterprise_api_key_setting_id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showSuccessToast(
        `API Key ${
          newStatus === "1" ? "Activated" : "Deactivated"
        } Successfully`,
      );

      fetchApiKeys(); // refresh table
    } catch (error) {
      console.error("Failed to change API key status:", error);

      showErrorToast(
        error.response?.data?.message || "Failed to update status",
      );
    }
  };

  // 🔹 Delete API Key (hard delete)
  const handleDeleteApiKey = async (record) => {
    if (!record) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete the API key for ${record.provider_name}?`,
    );
    if (!confirmDelete) return;

    try {
      const provider = record.provider_name.toLowerCase();

      // 1️⃣ Find all ONLINE models related to this provider
      const providerModelNames = availableModels
        .filter(
          (m) =>
            m.model_details?.model_type === "Online" &&
            (m.model_details?.model_provider || "").toLowerCase() === provider,
        )
        .map((m) => m.model_fid.toLowerCase());

      // 2️⃣ Update state (uncheck models)
      setModelSettings((prev) => {
        const updatedModels = prev.recommended_models.filter(
          (m) => !providerModelNames.includes(m.toLowerCase()),
        );

        return {
          ...prev,
          recommended_models: updatedModels,
        };
      });

      // 3️⃣ Delete API key
      await api.delete(
        `${API_BASE}/enterprise-api-keys/${record.enterprise_api_key_setting_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showSuccessToast("API Key deleted successfully ✔");
      fetchApiKeys();

      // 4️⃣ AUTO-SAVE AFTER STATE UPDATE
      // setTimeout(() => {
      //   handleSave(); // ✅ uses updated recommended_models
      // }, 0);
    } catch (error) {
      console.error("Failed to delete API key:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to delete API key",
      );
    }
  };

  // 🔹 Delete API Key (hard delete only)
  // const handleDeleteApiKey = async (record) => {
  //   if (!record) return;

  //   const confirmDelete = window.confirm(
  //     `Are you sure you want to permanently delete the API key for ${record.provider_name}?`
  //   );
  //   if (!confirmDelete) return;

  //   try {
  //     await api.delete(
  //       `${API_BASE}/enterprise-api-keys/${record.enterprise_api_key_setting_id}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     showSuccessToast("API Key deleted successfully ✔");

  //     // Refresh API key list
  //     fetchApiKeys();
  //   } catch (error) {
  //     console.error("Failed to delete API key:", error);
  //     showErrorToast(
  //       error.response?.data?.message || "Failed to delete API key"
  //     );
  //   }
  // };

  // 🔹 Copy API Key
  const handleCopyApiKey = (key) => {
    if (!key) return;
    navigator.clipboard
      .writeText(key)
      .then(() => {
        showSuccessToast("API Key copied to clipboard ✔");
      })
      .catch((err) => {
        console.error("Failed to copy API key:", err);
        showErrorToast("Failed to copy API key");
      });
  };

  // 🔹 Mask API Key (for table display)
  // const maskApiKey = (key) => {
  //   if (!key) return "—";
  //   const visibleStart = 4;
  //   const visibleEnd = 4;
  //   if (key.length <= visibleStart + visibleEnd) return key;
  //   const maskedMiddle = "*".repeat(key.length - visibleStart - visibleEnd);
  //   return key.slice(0, visibleStart) + maskedMiddle + key.slice(-visibleEnd);
  // };

  const providersWithModels = React.useMemo(() => {
    return [
      ...new Set(
        availableModels
          .filter(
            (m) => m.model_details?.model_type?.toLowerCase() === "online",
          )
          .map((m) => m.model_details?.model_provider?.toLowerCase())
          .filter(Boolean),
      ),
    ];
  }, [availableModels]);

  // console.log("providersWithModels:", providersWithModels);

  return (
    <div className="py-2 px-3 h-100 overflow-y-auto">
      <div className="">
        {/* === Subscription === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowSubscription(!showSubscription)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">1</div>
            <h5 className="section-title mb-0">Subscription Details</h5>
          </div>
          <i
            className={`bi ${
              showSubscription ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showSubscription && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Subscription Name</label>
                <select
                  className="form-select"
                  value={subscription.enterprise_subscription_name}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_name",
                  )}
                >
                  <option value="">Select Subscription</option>
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Subscription Duration</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_duration}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_duration",
                  )}
                />
              </div>
              <div className="col-md-4">
                <label>Subscription Price</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_prize}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_prize",
                  )}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4">
                <label>Subscription Start Date</label>
                <DatePicker
                  className="datecolor"
                  calendarClassName="datecolor" // calendar popup
                  selected={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : null
                  }
                  onChange={(date) => {
                    const iso = date?.toISOString().split("T")[0];
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_start_date: iso,
                      enterprise_subscription_end_date:
                        prev.enterprise_subscription_end_date < iso
                          ? ""
                          : prev.enterprise_subscription_end_date,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={new Date()}
                  customInput={<CustomDateInput />}
                />
              </div>

              <div className="col-md-4">
                <label>Subscription End Date</label>
                <DatePicker
                  selected={
                    subscription.enterprise_subscription_end_date
                      ? new Date(subscription.enterprise_subscription_end_date)
                      : null
                  }
                  onChange={(date) => {
                    const iso = date?.toISOString().split("T")[0];
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_end_date: iso,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : new Date()
                  }
                  customInput={<CustomDateInput />}
                />
              </div>
            </div>
          </>
        )}

        {/* === Organization === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowOrganization(!showOrganization)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">2</div>
            <h5 className="section-title mb-0">Organization Settings</h5>
          </div>
          <i
            className={`bi ${
              showOrganization ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showOrganization && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Theme</label>
                <select
                  className="form-select"
                  value={organization.enterprise_organisation_settings_theme}
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_theme",
                  )}
                >
                  <option value="">Select Theme</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Select Model</label>
                <select
                  className="form-select"
                  value={
                    organization.enterprise_organisation_settings_selected_model
                  }
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_selected_model",
                  )}
                >
                  <option value="">Choose Model</option>
                  <option value="gpt-3.5">GPT-3.5</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="custom">Custom Model</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Training Frequency</label>
                <select
                  className="form-select"
                  value={organization.training_frequency}
                  onChange={handleInput(setOrganization, "training_frequency")}
                >
                  <option value="">Select Frequency</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label>No Need To Store Data</label>
                <select
                  className="form-select"
                  value={organization.data_retention_period}
                  onChange={handleInput(
                    setOrganization,
                    "data_retention_period",
                  )}
                >
                  <option value="">Select Duration</option>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Total Allowed Document Size</label>
                <select
                  className="form-select"
                  value={organization.allowed_doc_size_mb}
                  onChange={handleInput(setOrganization, "allowed_doc_size_mb")}
                >
                  <option value="">Select Size</option>
                  <option value="100">100 MB</option>
                  <option value="500">500 MB</option>
                  <option value="1024">1 GB</option>
                </select>
              </div>
            </div>

            <div className="row mb-4"></div>
          </>
        )}

        {/* === Tenant Key === */}
        <EnterpriseTenantKeySection
          enterpriseId={enterpriseId}
          sectionNumber={3}
          defaultOpen={true}
        />

        <EnterpriseProfileSection
          sectionNumber={4}
          defaultOpen={true}
        />

        {/* === Security (Chat Messages Only) === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-3"
          style={{ cursor: "pointer" }}
          onClick={() => setShowChatSecurity(!showChatSecurity)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">5</div>
            <h5 className="section-title mb-0">Security (Chat Messages)</h5>
          </div>
          <i
            className={`bi ${
              showChatSecurity ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showChatSecurity && (
          <div className="mb-3">
            <div className="refresh-prompts mb-2">
              Applies only to stored chat messages (not files, not API keys, not
              system settings).
            </div>

            {isChatSecurityLoading ? (
              <div className="refresh-prompts fst-italic">
                Loading security settings…
              </div>
            ) : (
              <div className="row g-3">
                <div className="col-md-4">
                  <label>Encryption Mode</label>
                  <select
                    className="form-select"
                    value={chatSecurity.chat_encryption_mode}
                    onChange={(e) =>
                      setChatSecurity((prev) => ({
                        ...prev,
                        chat_encryption_mode: e.target.value,
                        ...(String(e.target.value).toLowerCase() === "none"
                          ? {
                              chat_key_management_mode: "platform_managed",
                              byok_key_base64: "",
                            }
                          : {}),
                      }))
                    }
                  >
                    <option value="none">None</option>
                    <option value="standard">Standard (Encrypt at Rest)</option>
                    <option value="end_to_end">
                      End-to-End (Client Payload Encryption)
                    </option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label>Key Management</label>
                  <select
                    className="form-select"
                    value={chatSecurity.chat_key_management_mode}
                    onChange={(e) =>
                      setChatSecurity((prev) => ({
                        ...prev,
                        chat_key_management_mode: e.target.value,
                        ...(String(e.target.value).toLowerCase() !==
                        "customer_managed_byok"
                          ? { byok_key_base64: "" }
                          : {}),
                      }))
                    }
                    disabled={chatSecurity.chat_encryption_mode === "none"}
                  >
                    <option value="platform_managed">Platform Managed</option>
                    <option value="customer_managed_byok">
                      Customer Managed (BYOK)
                    </option>
                  </select>
                </div>

                {chatSecurity.chat_encryption_mode !== "none" &&
                chatSecurity.chat_key_management_mode ===
                  "customer_managed_byok" ? (
                  <div className="col-md-4">
                    <label>BYOK (Base64, 32 bytes)</label>
                    <input
                      type="password"
                      className="form-control"
                      value={chatSecurity.byok_key_base64}
                      onChange={(e) =>
                        setChatSecurity((prev) => ({
                          ...prev,
                          byok_key_base64: e.target.value,
                        }))
                      }
                      placeholder="Paste base64 key (leave blank to keep existing)"
                      autoComplete="off"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* === Model Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-3"
          style={{ cursor: "pointer" }}
          onClick={() => setShowModelSettings(!showModelSettings)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">6</div>
            <h5 className="section-title mb-0">Model Settings</h5>
          </div>
          <i
            className={`bi ${
              showModelSettings ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showModelSettings && (
          <div className="mb-3">
            <h6 className="d-flex align-items-center">
              <img
                src={recommandedIcon}
                alt="Recommended Icon"
                style={{ width: "25px", height: "28px" }}
                className="me-2"
              />
              Recommended Models
            </h6>

            <div className="fw-bold mt-2 mb-2 refresh-prompts">
              OFFLINE MODELS
            </div>

            <div className="row mx-2 mb-3">
              {availableModels.filter(
                (m) => m.model_details?.model_type === "Offline",
              ).length === 0 ? (
                <div className="col-12 refresh-prompts fst-italic">
                  No offline models found. Please add or download an offline
                  model to continue.
                </div>
              ) : (
                availableModels
                  .filter((m) => m.model_details?.model_type === "Offline")
                  .map((model) => {
                    const modelName = model.model_fid;

                    const isChecked = modelSettings.recommended_models
                      ?.map((m) => m.toLowerCase())
                      .includes(modelName.toLowerCase());

                    return (
                      <div className="col-md-3 mb-2" key={modelName}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="custom-checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [
                                    ...modelSettings.recommended_models,
                                    modelName,
                                  ]
                                : modelSettings.recommended_models.filter(
                                    (m) =>
                                      m.toLowerCase() !==
                                      modelName.toLowerCase(),
                                  );

                              setModelSettings((prev) => ({
                                ...prev,
                                recommended_models: updated,
                              }));
                            }}
                          />
                          <label className="ms-2 form-check-label">
                            {model.model_details?.model_generic_name ||
                              modelName}
                          </label>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            <button
              type="button"
              className="custom-btn mt-2 mb-2 d-flex align-items-center gap-2"
              onClick={() => setShowApiModal(true)}
            >
              <img
                src={addIcon}
                alt="plus icon"
                style={{ width: "18px", height: "18px" }}
                className="icon-image"
              />
              Add API Key
            </button>

            <div className="table-responsive">
              <table className="table  mt-2 table-sm w-100">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>API Key</th>
                    <th>Online Models</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {apiKeys.length > 0 ? (
                    apiKeys.map((k) => {
                      const provider = k.provider_name.toLowerCase();

                      const providerModels = availableModels.filter(
                        (m) =>
                          m.model_details?.model_type === "Online" &&
                          (
                            m.model_details?.model_provider || ""
                          ).toLowerCase() === provider,
                      );

                      const hasApiKey = Boolean(k.api_key);

                      return (
                        <tr key={k.enterprise_api_key_setting_id}>
                          <td className="fw-semibold">{k.provider_name}</td>

                          <td
                            style={{
                              maxWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "clip",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {maskApiKey(k.api_key)}
                          </td>

                          <td>
                            <div className="d-flex flex-wrap">
                              {providerModels.length > 0 ? (
                                providerModels.map((model) => {
                                  const modelName = model.model_fid;

                                  const isChecked =
                                    modelSettings.recommended_models
                                      ?.map((m) => m.toLowerCase())
                                      .includes(modelName.toLowerCase());

                                  return (
                                    <div
                                      className="form-check me-3 align-items-center d-flex"
                                      key={modelName}
                                    >
                                      <input
                                        type="checkbox"
                                        className="custom-checkbox"
                                        disabled={!hasApiKey}
                                        checked={hasApiKey && isChecked}
                                        onChange={(e) => {
                                          if (!hasApiKey) return;

                                          const updated = e.target.checked
                                            ? [
                                                ...modelSettings.recommended_models,
                                                modelName,
                                              ]
                                            : modelSettings.recommended_models.filter(
                                                (m) =>
                                                  m.toLowerCase() !==
                                                  modelName.toLowerCase(),
                                              );

                                          setModelSettings((prev) => ({
                                            ...prev,
                                            recommended_models: updated,
                                          }));
                                        }}
                                      />
                                      <label
                                        className={`ms-1 form-check-label ${
                                          !hasApiKey ? "refresh-prompts" : ""
                                        }`}
                                      >
                                        {model.model_details
                                          ?.model_generic_name || modelName}
                                      </label>
                                    </div>
                                  );
                                })
                              ) : (
                                <span className="refresh-prompts">
                                  No models
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="gap-2">
                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => handleCopyApiKey(k.api_key)}
                              title="Copy API Key"
                            >
                              <img
                                src={cpyIcon}
                                className="icon-image"
                                alt="Copy"
                              />
                            </button>

                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => handleEdit(k)}
                              title="Edit"
                            >
                              <img
                                src={editIcon}
                                className="icon-image"
                                alt="Edit"
                              />
                            </button>

                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => handleDeleteApiKey(k)}
                              title="Delete"
                            >
                              <img
                                src={crossIcon}
                                className="icon-image"
                                alt="Delete"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center refresh-prompts">
                        No API keys found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* === Documentation Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDocumentation(!showDocumentation)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">7</div>
            <h5 className="section-title mb-0">Documentation Settings</h5>
          </div>
          <i
            className={`bi ${
              showDocumentation ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showDocumentation && (
          <div className="row mb-4">
            <div className="col-md-4 d-flex align-items-center">
              <label className="me-3 mb-0">Auto Approve</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={documentation.auto_approve || false}
                  onChange={(e) =>
                    setDocumentation((prev) => ({
                      ...prev,
                      auto_approve: e.target.checked,
                    }))
                  }
                />
              </div>
            </div>

            <div className="col-md-4">
              <label>Max Files Per Upload</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={documentation.max_upload_file_count ?? 20}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    max_upload_file_count: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                Maximum number of files a user can upload at once.
              </small>
            </div>

            <div className="col-md-4">
              <label>Project Capacity</label>
              <div className="input-group">
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={documentation.project_capacity_limit_value ?? 75}
                  onChange={(e) =>
                    setDocumentation((prev) => ({
                      ...prev,
                      project_capacity_limit_value: e.target.value,
                    }))
                  }
                />
                <select
                  className="form-select"
                  value={documentation.project_capacity_limit_unit ?? "MB"}
                  onChange={(e) =>
                    setDocumentation((prev) => ({
                      ...prev,
                      project_capacity_limit_unit: e.target.value,
                    }))
                  }
                  style={{ maxWidth: "90px" }}
                >
                  <option value="MB">MB</option>
                  <option value="GB">GB</option>
                </select>
              </div>
              <small className="text-muted">
                Maximum total size allowed across uploaded project files.
              </small>
            </div>

            <div className="col-md-4">
              <label>Max Files Per Project</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={documentation.project_max_files_per_project ?? 20}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    project_max_files_per_project: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                Maximum number of files allowed in a project.
              </small>
            </div>

            <div className="col-md-4">
              <label>ZIP/RAR Files Max Size (MB)</label>
              <input
                type="number"
                min="1"
                max="2048"
                className="form-control"
                value={documentation.max_upload_zip_size_mb ?? 400}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    max_upload_zip_size_mb: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                .zip, .rar, .7z, .tar, .gz
              </small>
            </div>

            <div className="col-md-4">
              <label>Documents Max Size (MB)</label>
              <input
                type="number"
                min="1"
                max="2048"
                className="form-control"
                value={documentation.max_upload_document_size_mb ?? 100}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    max_upload_document_size_mb: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                .pdf, .doc, .docx, .ppt, .pptx, .xls, .xlsx
              </small>
            </div>

            <div className="col-md-4">
              <label>Media Files Max Size (MB)</label>
              <input
                type="number"
                min="1"
                max="2048"
                className="form-control"
                value={documentation.max_upload_media_size_mb ?? 50}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    max_upload_media_size_mb: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                Videos (.mp4, .avi, .mov, .mkv) &amp; audio (.mp3, .wav, .aac)
              </small>
            </div>

            <div className="col-md-4">
              <label>Other Files Max Size (MB)</label>
              <input
                type="number"
                min="1"
                max="2048"
                className="form-control"
                value={documentation.max_upload_other_size_mb ?? 50}
                onChange={(e) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    max_upload_other_size_mb: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                All remaining supported file types
              </small>
            </div>

            <div className="col-12 mt-3">
              <AllowedExtensionsEditor
                value={documentation.allowed_upload_extensions}
                onChange={(extensions) =>
                  setDocumentation((prev) => ({
                    ...prev,
                    allowed_upload_extensions: extensions,
                  }))
                }
              />
            </div>
          </div>
        )}

        {/* === Template Setting === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowTemplateSettings(!showTemplateSettings)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">8</div>
            <h5 className="section-title mb-0">Template Setting</h5>
          </div>
          <i
            className={`bi ${
              showTemplateSettings ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showTemplateSettings && (
          <TemplateSettingsPanel
            isLoadingTheme={isLoadingTheme}
            templates={presetTemplates}
            selectedTemplate={selectedTemplate}
            themeSettings={themeSettings}
            templateColors={templateColors}
            logoPreviewUrl={logoPreviewUrl}
            onTemplateSelect={handleTemplateSelect}
            onThemeFieldChange={updateThemeSetting}
            onThemeColorChange={updateTemplateColorSetting}
            onLogoUpload={() =>
              document.getElementById("logoUploadInput")?.click()
            }
            onLogoRemove={() => {
              setLogoFile(null);
              setLogoPreviewUrl(null);
              setThemeSettings((prev) => ({
                ...prev,
                logo_path: "",
                logo_key: "",
              }));
            }}
            onLogoChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setLogoFile(file);
                setLogoPreviewUrl(URL.createObjectURL(file));
                setShowCropModal(true);
              }
            }}
            onBrandingToggle={(checked) =>
              updateThemeSetting("enable_logo", checked)
            }
            onTemplateSave={handleTemplateSave}
            isSavingThemeOnly={isSavingThemeOnly}
          />
        )}

        {/* === Glossary Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowGlossary(!showGlossary)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">9</div>
            <h5 className="section-title mb-0">Glossary Settings</h5>
          </div>
          <i
            className={`bi ${showGlossary ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}
          ></i>
        </div>

        {showGlossary && (
          <div className="glossary-panel">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="glossary-actions">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={handleAddGlossaryRow}
                >
                  + Add Row
                </button>
                <button
                  type="button"
                  className="custom-btn"
                  onClick={handleSaveGlossary}
                  disabled={isGlossarySaving}
                >
                  {isGlossarySaving ? "Saving..." : "Save Glossary"}
                </button>

                <button
                  type="button"
                  className="cancle-btn"
                  onClick={handleDeleteGlossary}
                  disabled={isGlossaryDeleting}
                >
                  {isGlossaryDeleting ? "Deleting..." : "Delete Glossary Data"}
                </button>
              </div>
            </div>

            <div className="table-responsive p-0">
              <table className="table glossary-table table-sm w-100">
                <thead>
                  <tr>
                    <th>Term</th>
                    <th>Full Form</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGlossary && paginatedGlossary.length > 0 ? (
                    paginatedGlossary.map((item) => (
                      <tr key={`glossary-${item.originalIndex}`}>
                        <td style={{ minWidth: "150px" }}>
                          <input
                            className="form-control"
                            value={item.term || ""}
                            onChange={(e) =>
                              handleGlossaryChange(
                                item.originalIndex,
                                "term",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td style={{ minWidth: "180px" }}>
                          <input
                            className="form-control"
                            value={item.full_form || ""}
                            onChange={(e) =>
                              handleGlossaryChange(
                                item.originalIndex,
                                "full_form",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            className="form-control"
                            rows={2}
                            value={item.description || ""}
                            onChange={(e) =>
                              handleGlossaryChange(
                                item.originalIndex,
                                "description",
                                e.target.value,
                              )
                            }
                          />
                        </td>

                        <td className="d-flex justify-content-center">
                          <button
                            type="button"
                            className="icon-button"
                            onClick={() =>
                              handleRemoveGlossaryRow(item.originalIndex)
                            }
                            title="Remove"
                          >
                            <img
                              src={crossIcon}
                              className="icon-image"
                              alt="Remove"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center refresh-prompts">
                        No glossary entries available. Click "Add Row" to create
                        your first glossary entry.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {/* {filteredGlossary.length > 0 && (
              <div className="table-footer bgcolor d-flex justify-content-between align-items-center mt-2 p-2 rounded">
                <div className="refresh-prompts">
                  Showing {(glossaryCurrentPage - 1) * glossaryEntriesPerPage + 1} to {Math.min(glossaryCurrentPage * glossaryEntriesPerPage, filteredGlossary.length)} of {filteredGlossary.length} entries
                </div>
                <ul className="pagination mb-0">
                  <li className={`page-item ${glossaryCurrentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setGlossaryCurrentPage(1)}>
                      <i className="bi bi-chevron-double-left" />
                    </button>
                  </li>
                  <li className={`page-item ${glossaryCurrentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setGlossaryCurrentPage(glossaryCurrentPage - 1)}>
                      <i className="bi bi-chevron-left" />
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">Page {glossaryCurrentPage} of {totalGlossaryPages}</span>
                  </li>
                  <li className={`page-item ${glossaryCurrentPage === totalGlossaryPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setGlossaryCurrentPage(glossaryCurrentPage + 1)}>
                      <i className="bi bi-chevron-right" />
                    </button>
                  </li>
                  <li className={`page-item ${glossaryCurrentPage === totalGlossaryPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setGlossaryCurrentPage(totalGlossaryPages)}>
                      <i className="bi bi-chevron-double-right" />
                    </button>
                  </li>
                </ul>
              </div>
            )} */}
          </div>
        )}

        <hr className="my-1" />

        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="cancle-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
          >
            Reset to Default
          </button>
          <button
            type="button"
            className="custom-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Configuration"
            )}
          </button>
        </div>
        {/* <hr className="mt-5" /> */}
      </div>

      {showApiModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-history modal-dialog-centered history-model">
            <div className="modal-content position-relative">
              {/* ❌ Close Button */}
              <button
                type="button"
                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                onClick={() => {
                  setShowApiModal(false);
                  setEditingRecord(null);
                  setApiKey("");
                  setSelectedProvider("OpenAI");
                }}
              >
                <img
                  src={crossIcon}
                  className="icon-image cross-i"
                  alt="Close"
                />
              </button>

              {/* -------- Modal Body -------- */}
              <div className="p-4">
                <p className="fw-bold text-center mt-2 mb-4 refresh-prompts">
                  {editingRecord ? "Update API Key" : "Add API Key"}
                </p>

                {/* 🔽 Provider Select */}
                <label className="fw-semibold mb-1">Select Provider</label>
                <select
                  className="form-select mb-3"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={!!editingRecord}
                >
                  {providersWithModels.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>

                {/* 🔑 API Key Input */}
                <label className="fw-semibold mb-1">
                  {editingRecord ? "New API Key" : "API Key"}
                </label>

                <input
                  className="form-control"
                  rows="3"
                  placeholder={
                    editingRecord
                      ? "Paste new API key to replace existing one..."
                      : "Paste your API key here..."
                  }
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />

                {editingRecord && (
                  <small className="refresh-prompts d-block mt-1">
                    Existing API key will remain unchanged if validation or
                    update fails.
                  </small>
                )}
              </div>

              {/* -------- Footer Buttons -------- */}
              <div className="modal-footer justify-content-center gap-2 mb-3">
                <button
                  type="button"
                  className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                  onClick={() => {
                    setShowApiModal(false);
                    setEditingRecord(null);
                    setApiKey("");
                    setSelectedProvider("OpenAI");
                  }}
                  disabled={isValidating}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                  onClick={handleValidateApi}
                  disabled={isValidating}
                >
                  {isValidating
                    ? "Validating..."
                    : editingRecord
                      ? "Validate & Update"
                      : "Validate & Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🖼️ Logo Adjustment Modal */}
      {showCropModal && (
        <div
          className="modal modal-backdrop-custom d-block"
          tabIndex="-1"
          style={{ zIndex: 2000 }}
        >
          <div
            className="modal-dialog modal-history modal-dialog-centered"
            style={{ maxWidth: "800px" }}
          >
            <div className="modal-content">
              <div className="modal-header border-bottom p-3">
                <h5 className="modal-title fw-bold">Adjust Logo (Landscape)</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCropModal(false)}
                ></button>
              </div>
              <div
                className="modal-body p-0 text-center"
                style={{
                  position: "relative",
                  height: "50vh",
                  minHeight: "300px",
                  background: "#333",
                }}
              >
                <Cropper
                  image={logoPreviewUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={4 / 1}
                  onCropChange={setCrop}
                  onCropComplete={(croppedArea, croppedAreaPixels) =>
                    setCroppedAreaPixels(croppedAreaPixels)
                  }
                  onZoomChange={setZoom}
                />
              </div>
              <div className="modal-footer flex-column p-3">
                <div className="w-100 mb-3 px-4">
                  <label className="form-label d-flex justify-content-between small fw-bold text-muted">
                    <span>Zoom Level</span>
                    <span>{Math.round(zoom * 100)}%</span>
                  </label>
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(e.target.value)}
                    className="form-range"
                  />
                </div>
                <div className="d-flex justify-content-center gap-2 w-100">
                  <button
                    type="button"
                    className="btn cancle-btn px-4"
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreviewUrl(null);
                      setShowCropModal(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn custom-btn px-4"
                    disabled={isProcessingLogo}
                    onClick={async () => {
                      try {
                        setIsProcessingLogo(true);
                        const croppedImageBlob = await getCroppedImg(
                          logoPreviewUrl,
                          croppedAreaPixels,
                        );
                        const croppedImageUrl =
                          URL.createObjectURL(croppedImageBlob);

                        // Update the file and preview with the cropped version
                        const file = new File(
                          [croppedImageBlob],
                          "logo_cropped.png",
                          { type: "image/png" },
                        );
                        setLogoFile(file);
                        setLogoPreviewUrl(croppedImageUrl);
                        setIsProcessingLogo(false);
                        setShowCropModal(false);
                        showSuccessToast("Logo adjusted successfully");
                      } catch (e) {
                        setIsProcessingLogo(false);
                        console.error(e);
                        showErrorToast("Failed to crop image");
                      }
                    }}
                  >
                    {isProcessingLogo ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      "Confirm & Crop"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseSettings;
